import { Tacho, TachoReading } from "@/reducers/tachosSlice"
import { formatClockTime, isSameWarsawDay } from "./warsawTime"

export const STALE_TACHO_AFTER_MS = 15 * 60 * 1000
const WEEKLY_DRIVING_LIMIT_SECONDS = 56 * 3600
const BIWEEKLY_DRIVING_LIMIT_SECONDS = 90 * 3600
const BREAK_DUE_SOON_SECONDS = 3600
const RESTING_ACTIVITIES = new Set(["break", "rest"])

export type ClockTime = {
  at: number
  label: string
}

export type DrivingLimit = {
  doneSeconds: number | null
  leftSeconds: number | null
  limitSeconds: number
  usedShare: number | null
}

export type BreakDue = {
  drivingLeftSeconds: number
  startsAt: ClockTime
  endsAt: ClockTime | null
  position: number
  dueSoon: boolean
}

export type WeeklyRest = {
  startsBy: ClockTime | null
  requiredSeconds: number | null
  inProgress: boolean
}

export type Resting = {
  since: ClockTime
  seconds: number
}

export type TachoFreshness = {
  at: ClockTime
  ageSeconds: number
  stale: boolean
}

export type TodayFigures = {
  doneSeconds: number
  leftSeconds: number
  availableSeconds: number
  usedShare: number
  nextBreak: BreakDue | null
  dayEndsAt: ClockTime | null
  dailyExtensionsLeft: number | null
}

export type DriverWorkTime = {
  key: string
  driverName: string | null
  doubleCrew: boolean
  activity: string | null
  resting: Resting | null
  freshness: TachoFreshness | null
  today: TodayFigures
  week: DrivingLimit & { weeklyRest: WeeklyRest | null }
  twoWeeks: DrivingLimit
}

type Clock = (at: number) => ClockTime

const drivingLimit = (
  doneSeconds: number | null,
  leftSeconds: number | null,
  limitSeconds: number,
): DrivingLimit => ({
  doneSeconds,
  leftSeconds,
  limitSeconds,
  usedShare:
    doneSeconds === null ? null : Math.min(1, doneSeconds / limitSeconds),
})

const currentDayEnd = (reading: TachoReading): number | null => {
  const { dayEndAt: dayEnd, publishedAt: readAt } = reading
  if (dayEnd === null || readAt === null) return dayEnd
  const leftOverFromEarlierDay =
    dayEnd < readAt && !isSameWarsawDay(dayEnd, readAt)
  return leftOverFromEarlierDay ? null : dayEnd
}

const secondsBetween = (from: number, to: number) =>
  Math.max(0, Math.floor((to - from) / 1000))

const nextBreakOf = (
  reading: TachoReading,
  doneToday: number,
  leftToday: number,
  clock: Clock,
): BreakDue | null => {
  const { publishedAt: readAt, remainingSecondsOfUninterruptedDriving: left } =
    reading
  if (
    readAt === null ||
    left === null ||
    left >= leftToday ||
    RESTING_ACTIVITIES.has(reading.currentActivity ?? "")
  ) {
    return null
  }
  const startsAt = readAt + left * 1000
  const length = reading.remainingMiddayBreakSeconds
  return {
    drivingLeftSeconds: left,
    startsAt: clock(startsAt),
    endsAt: length ? clock(startsAt + length * 1000) : null,
    position: (doneToday + left) / (doneToday + leftToday),
    dueSoon: left <= BREAK_DUE_SOON_SECONDS,
  }
}

const todayOf = (reading: TachoReading, clock: Clock): TodayFigures => {
  const readAt = reading.publishedAt
  const dayEnd = currentDayEnd(reading)
  const done = reading.usedSecondsToday
  const left =
    dayEnd !== null && readAt !== null
      ? Math.min(reading.remainingSecondsToday, secondsBetween(readAt, dayEnd))
      : reading.remainingSecondsToday
  const available = done + left
  return {
    doneSeconds: done,
    leftSeconds: left,
    availableSeconds: available,
    usedShare: available > 0 ? done / available : 0,
    nextBreak: nextBreakOf(reading, done, left, clock),
    dayEndsAt: dayEnd === null ? null : clock(dayEnd),
    dailyExtensionsLeft: reading.remainingDailyExtensions,
  }
}

const weeklyRestOf = (
  reading: TachoReading,
  clock: Clock,
): WeeklyRest | null => {
  const {
    weeklyRestDueAt: dueAt,
    weeklyRestRequiredSeconds: requiredSeconds,
    weeklyRestInProgress: inProgress,
  } = reading
  if (dueAt === null && requiredSeconds === null && !inProgress) return null
  return {
    startsBy: dueAt === null ? null : clock(dueAt),
    requiredSeconds,
    inProgress,
  }
}

// Only providers that report when the ongoing rest began (rest_started_at)
// can say how long the driver has rested; the rest show the activity alone.
const restingOf = (
  reading: TachoReading,
  now: number,
  clock: Clock,
): Resting | null => {
  const since = reading.restStartedAt
  if (
    since === null ||
    since > now ||
    !RESTING_ACTIVITIES.has(reading.currentActivity ?? "")
  ) {
    return null
  }
  return { since: clock(since), seconds: secondsBetween(since, now) }
}

const freshnessOf = (
  readAt: number | null,
  now: number,
  clock: Clock,
): TachoFreshness | null =>
  readAt === null
    ? null
    : {
        at: clock(readAt),
        ageSeconds: secondsBetween(readAt, now),
        stale: now - readAt > STALE_TACHO_AFTER_MS,
      }

const workTimeOf = (
  reading: TachoReading,
  now: number,
  timeZone: string | undefined,
): Omit<DriverWorkTime, "key" | "doubleCrew"> => {
  const clock: Clock = (at) => ({
    at,
    label: formatClockTime(at, now, timeZone),
  })
  return {
    driverName: reading.driverName,
    activity: reading.currentActivity,
    resting: restingOf(reading, now, clock),
    freshness: freshnessOf(reading.publishedAt, now, clock),
    today: todayOf(reading, clock),
    week: {
      ...drivingLimit(
        reading.legalCounters?.weeklyDriving?.total ??
          reading.usedSecondsThisWeek,
        reading.remainingSecondsThisWeek,
        WEEKLY_DRIVING_LIMIT_SECONDS,
      ),
      weeklyRest: weeklyRestOf(reading, clock),
    },
    twoWeeks: drivingLimit(
      reading.legalCounters?.biweeklyDriving?.total ?? null,
      reading.remainingSecondsTwoWeeks,
      BIWEEKLY_DRIVING_LIMIT_SECONDS,
    ),
  }
}

export const leadWorkTime = (
  workTimes: DriverWorkTime[],
): DriverWorkTime | null =>
  workTimes.find((workTime) => workTime.activity === "driving") ??
  workTimes[0] ??
  null

export const driverWorkTime = (
  tacho: Tacho,
  now: number,
  timeZone?: string,
): DriverWorkTime[] => {
  if (tacho.crewReadings?.length) {
    return tacho.crewReadings.map((member, index) => ({
      key: `${tacho.jid}/${member.driverId ?? index}`,
      doubleCrew: true,
      ...workTimeOf(member, now, timeZone),
    }))
  }
  if (!tacho.reading) return []
  return [
    {
      key: tacho.jid,
      doubleCrew: tacho.reading.doubleCrew,
      ...workTimeOf(tacho.reading, now, timeZone),
    },
  ]
}

export const chatWorkTime = (
  tachos: Tacho[],
  doubleCrew: boolean,
  now: number,
  timeZone?: string,
): DriverWorkTime[] =>
  tachos
    .flatMap((tacho) => driverWorkTime(tacho, now, timeZone))
    .map((workTime) => ({
      ...workTime,
      doubleCrew: doubleCrew || workTime.doubleCrew,
    }))
